/**
 * Client-side observability hooks. Never log tokens, cookies, or full request bodies.
 * Wire `window.__CMNDF_REPORT_ERROR__` in production if you use another reporter.
 */

import { isAxiosError } from 'axios';

export type ClientErrorContext = 'boundary' | 'query' | 'mutation' | 'axios';

function getReporter(): ((err: unknown, context: ClientErrorContext) => void) | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { __CMNDF_REPORT_ERROR__?: (e: unknown, c: ClientErrorContext) => void })
    .__CMNDF_REPORT_ERROR__;
}

/** Safe, non-PII summary for logging / external reporters */
export function toSafeErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message || err.name;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err).slice(0, 500);
  } catch {
    return 'Unknown error';
  }
}

/**
 * Whether this failure is worth forwarding to a reporter (avoids noise from expected 4xx).
 */
export function shouldReportHttpFailure(err: unknown): boolean {
  if (!isAxiosError(err)) {
    return true;
  }
  const status = err.response?.status;
  if (status === undefined) {
    return true;
  }
  if (status >= 500) {
    return true;
  }
  if (status === 408 || status === 429) {
    return true;
  }
  return false;
}

export function reportClientError(err: unknown, context: ClientErrorContext = 'query'): void {
  const reporter = getReporter();
  if (typeof reporter === 'function') {
    try {
      reporter(err, context);
    } catch {
      /* ignore */
    }
  }

  if (context !== 'boundary' && !shouldReportHttpFailure(err)) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(`[CMNDF ${context}]`, toSafeErrorMessage(err));
    }
    return;
  }

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(`[CMNDF ${context}]`, toSafeErrorMessage(err));
  }
}

/** Axios response error path — uses same filtering as queries. */
export function reportAxiosFailure(err: unknown): void {
  reportClientError(err, 'axios');
}

const SLOW_MS = 3000;

function scrubUrl(url: string): string {
  return url.replace(/([?&])(token|access_token|refresh_token|password|secret)=[^&]*/gi, '$1$2=<redacted>');
}

/**
 * Log slow API calls in development only (>3s). Production: use backend APM; optional `__CMNDF_REPORT_ERROR__` for custom hooks.
 */
export function logSlowHttpRequest(method: string, url: string, durationMs: number): void {
  if (durationMs < SLOW_MS) {
    return;
  }
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  const safeUrl = scrubUrl(url);
  // eslint-disable-next-line no-console
  console.warn(`[CMNDF slow API] ${method} ${safeUrl} took ${durationMs}ms`);
}

/** @deprecated Use logSlowHttpRequest */
export function logSlowRequestDev(method: string, url: string, durationMs: number): void {
  logSlowHttpRequest(method, url, durationMs);
}
